import { Resolver, transformToNestObject } from 'react-hook-form';
import * as Yup from 'yup';

// This helper method basically transforms yup validation errors into
// an error object that react-hook-form understands.
// It converts an array of errors into an object, keyed by path:
// ```
//  [{path,message,type}] => {[path] => {message, type}}
// ```
// By default it will return only a single error type per path (since we're
// mostly interested in showing one field error at a time and we can bail on
// other failed validations), but it can optionally return all the error types
// for every field path.
const parseErrorSchema = (error: Yup.ValidationError, validateAllFieldCriteria: boolean) =>
  Array.isArray(error.inner) && error.inner.length
    ? error.inner.reduce((previous: Record<string, any>, { path, message, type }) => {
        const previousTypes = (previous[path] && previous[path].types) || {};
        return {
          ...previous,
          ...(path
            ? {
                [path]: {
                  ...(previous[path] || {
                    message,
                    type,
                  }),
                  ...(validateAllFieldCriteria
                    ? {
                        types: {
                          ...previousTypes,
                          [type]: previousTypes[type]
                            ? [...[].concat(previousTypes[type]), message]
                            : message,
                        },
                      }
                    : {}),
                },
              }
            : {}),
        };
      }, {})
    : {
        [error.path]: { message: error.message, type: error.type },
      };

export const yupResolver =
  <TFieldValues extends Record<string, any>>(
    schema: Yup.ObjectSchema | Yup.Lazy,
    options: Omit<Yup.ValidateOptions, 'context'> = {
      abortEarly: false,
    }
  ): Resolver<TFieldValues> =>
  async (values, context, validateAllFieldCriteria = false) => {
    try {
      if ((options as Yup.ValidateOptions).context && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          "You should not used the yup options context. Please, use the 'useForm' context object instead"
        );
      }
      return {
        values: (await schema.validate(values, {
          ...options,
          context,
        })) as any,
        errors: {},
      };
    } catch (e) {
      const parsedErrors = parseErrorSchema(e, validateAllFieldCriteria);
      return {
        values: {},
        errors: validateAllFieldCriteria ? parsedErrors : transformToNestObject(parsedErrors),
      };
    }
  };

export default yupResolver;
